import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ProjectCard, ProjectCardSkeleton } from '../components/ProjectCard';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  TrendingUp,
  Rocket,
  Users,
  Star,
  Clock,
  Filter,
  Plus
} from 'lucide-react';
import { projectAPI, categoryAPI } from '../lib/api';
import { FEED_TYPES } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';

const FEED_ICONS = {
  trending: TrendingUp,
  rising: Rocket,
  community: Users,
  smart_picks: Star,
  latest: Clock,
};

export function DiscoverPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const activeFeed = searchParams.get('feed') || 'trending';
  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryAPI.list();
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const response = await projectAPI.list({
          feed: activeFeed,
          category: activeCategory || undefined,
          limit: 20,
        });
        setProjects(response.data.projects);
        setTotal(response.data.total);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [activeFeed, activeCategory]);

  const handleFeedChange = (feed) => {
    setSearchParams(prev => {
      prev.set('feed', feed);
      return prev;
    });
  };

  const handleCategoryChange = (category) => {
    setSearchParams(prev => {
      if (category && category !== 'all') {
        prev.set('category', category);
      } else {
        prev.delete('category');
      }
      return prev;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('discover.title')}</h1>
              <p className="text-slate-600 mt-1">
                {t('discover.subtitle')}
              </p>
            </div>
            {isAuthenticated && (
              <Link to="/submit">
                <Button className="btn-boost" data-testid="submit-btn-header">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('nav.submitProject')}
                </Button>
              </Link>
            )}
          </div>

          {/* Feed Tabs */}
          <div className="flex flex-wrap gap-2 mt-6">
            {FEED_TYPES.map((feed) => {
              const Icon = FEED_ICONS[feed.id];
              return (
                <button
                  key={feed.id}
                  onClick={() => handleFeedChange(feed.id)}
                  className={`feed-tab ${activeFeed === feed.id ? 'active' : ''}`}
                  data-testid={`feed-tab-${feed.id}`}
                >
                  <Icon className="w-4 h-4 inline mr-1.5" />
                  {t(feed.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters & Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-600">{t('discover.filters')}</span>
          </div>

          <Select value={activeCategory || 'all'} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-48" data-testid="category-filter">
              <SelectValue placeholder={t('discover.allCategories')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('discover.allCategories')}</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto text-sm text-slate-500">
            {t('discover.projectsFound', { count: total })}
          </div>
        </div>

        {/* Category Pills */}
        {activeCategory && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-slate-600">{t('discover.filteredBy')}</span>
            <Badge
              variant="secondary"
              className="capitalize cursor-pointer hover:bg-slate-200"
              onClick={() => handleCategoryChange('')}
            >
              {activeCategory}
              <span className="ml-1">×</span>
            </Badge>
          </div>
        )}

        {/* Projects Grid */}
        <div className="bento-grid">
          {loading ? (
            <>
              <ProjectCardSkeleton />
              <ProjectCardSkeleton />
              <ProjectCardSkeleton />
              <ProjectCardSkeleton />
              <ProjectCardSkeleton />
              <ProjectCardSkeleton />
            </>
          ) : projects.length > 0 ? (
            projects.map((project, index) => (
              <ProjectCard
                key={project.project_id}
                project={project}
                featured={index === 0 && activeFeed === 'trending'}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-16">
              <Rocket className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                {t('discover.noProjects')}
              </h3>
              <p className="text-slate-500 mb-6">
                {activeCategory
                  ? t('discover.noCategoryProjects')
                  : t('discover.beFirstSubmit')}
              </p>
              {isAuthenticated && (
                <Link to="/submit">
                  <Button className="btn-boost">{t('landing.submitYourProject')}</Button>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Load More */}
        {projects.length > 0 && projects.length < total && (
          <div className="text-center mt-12">
            <Button variant="outline" className="rounded-full px-8">
              {t('discover.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
